<template>
  <div class="modal" :class="{ 'modal-open': visible === true }">
    <div class="modal-box w-11/12 max-w-xl">
      <h3 class="font-bold text-lg mb-4">{{ isEdit === true ? 'Edit Source' : 'Add Source' }}</h3>

      <div class="mb-4">
        <label class="label"><span class="label-text">Channel</span></label>
        <div class="selector-row selector-row-channel">
          <button
            v-for="channelOption in channelOptions"
            :key="channelOption.key"
            type="button"
            :disabled="isEdit === true"
            :class="['select-chip', 'select-chip-channel', selectedChannel === channelOption.key ? 'select-chip-active' : '']"
            @click="selectChannel(channelOption.key)"
          >
            <span class="select-chip-content">
              <Icon :icon="channelOption.icon" width="16" />
              <span class="select-chip-label">{{ channelOption.label }}</span>
            </span>
          </button>
        </div>
      </div>

      <div class="mb-4">
        <label class="label"><span class="label-text">Provider</span></label>
        <div class="selector-row selector-row-provider">
          <button
            v-for="providerOption in providerOptions"
            :key="providerOption.key"
            type="button"
            :disabled="isEdit === true"
            :class="['select-chip', providerButtonClass, selectedProvider === providerOption.key ? 'select-chip-active' : '']"
            @click="selectProvider(providerOption.key)"
          >
            <span class="select-chip-content">
              <Icon :icon="providerOption.icon" width="16" />
              <span class="select-chip-label">{{ providerOption.label }}</span>
            </span>
          </button>
        </div>
      </div>

      <div class="source-fields">
        <div v-if="selectedProvider === 'meta'">
          <div>
            <label class="label"><span class="label-text">WhatsApp Business Account ID</span></label>
            <input type="text" v-model="localForm.wabaId" autocomplete="off" class="input input-bordered w-full" placeholder="e.g. 1314907454054694" />
          </div>
          <div>
            <label class="label"><span class="label-text">Phone Number ID</span></label>
            <input type="text" v-model="localForm.phoneNumberId" autocomplete="off" class="input input-bordered w-full" placeholder="e.g. 1322751940913843" />
          </div>
          <div>
            <label class="label"><span class="label-text">Phone Number</span></label>
            <input type="text" v-model="localForm.phoneNumber" autocomplete="off" class="input input-bordered w-full" placeholder="+1234567890" />
          </div>
          <div>
            <label class="label"><span class="label-text">API Key (Meta Access Token)</span></label>
            <input type="password" v-model="localForm.apiKey" autocomplete="new-password" class="input input-bordered w-full" placeholder="EAAB... or EAAT... (Access Token, not URL)" />
          </div>
          <div>
            <label class="label"><span class="label-text">Meta App Secret</span></label>
            <input type="password" v-model="localForm.appSecret" autocomplete="new-password" class="input input-bordered w-full" placeholder="32-character hex App Secret" />
          </div>
          <div>
            <label class="label"><span class="label-text">Meta API Proxy Domain (Normal)</span></label>
            <input type="text" v-model="localForm.customDomain" autocomplete="off" class="input input-bordered w-full" placeholder="https://proxy.example.com" />
            <span class="text-xs text-base-content/70">Optional third-party relay that forwards requests to the Meta API unchanged.</span>
          </div>
          <div class="mt-4 rounded-lg border border-base-300 p-4">
            <label class="label cursor-pointer">
              <span class="label-text">Enable WhatsApp Voice</span>
              <input type="checkbox" v-model="localForm.voiceEnabled" class="checkbox" />
            </label>
            <div v-if="localForm.voiceEnabled === true" class="mt-3">
              <label class="label"><span class="label-text">Call button visibility</span></label>
              <select v-model="localForm.voiceCallIconVisibility" class="select select-bordered w-full">
                <option value="default">Show call button</option>
                <option value="disableAll">Hide unsolicited call entry points</option>
              </select>
              <label class="label mt-3 cursor-pointer">
                <span class="label-text">Request callback permission after inbound calls</span>
                <input type="checkbox" v-model="localForm.voiceCallbackPermissionEnabled" class="checkbox" />
              </label>
              <div class="mt-3">
                <label class="label"><span class="label-text">Meta API Proxy Domain (Voice)</span></label>
                <input type="text" v-model="localForm.voiceCustomDomain" autocomplete="off" class="input input-bordered w-full" placeholder="https://voice-proxy.example.com" />
                <span class="text-xs text-base-content/70">Optional third-party relay for WhatsApp Calling API requests.</span>
              </div>
              <div class="mt-4 pt-3 border-t border-base-300">
                <label class="label cursor-pointer justify-start">
                  <input type="checkbox" v-model="localForm.createCallPermissionTemplate" class="checkbox" />
                  <span class="label-text ml-2 font-medium">Create outbound call permission template</span>
                </label>
                <div v-if="localForm.createCallPermissionTemplate === true" class="mt-2 pl-6">
                  <span class="text-xs text-base-content/70 block mb-2">
                    Creates an approved Meta WhatsApp template to request permission before placing outbound calls to recipients in Quick Outbound.
                  </span>
                  <div class="mb-2">
                    <label class="label py-1"><span class="label-text text-xs">Template Language</span></label>
                    <select v-model="localForm.callPermissionTemplateLanguage" class="select select-bordered select-sm w-full" @change="handleCallPermissionLanguageChange">
                      <option v-for="opt in callPermissionLanguageOptions" :key="opt.code" :value="opt.code">
                        {{ opt.label }}
                      </option>
                    </select>
                  </div>
                  <div class="mb-2">
                    <label class="label py-1"><span class="label-text text-xs">Template Name</span></label>
                    <input type="text" v-model="localForm.callPermissionTemplateName" class="input input-bordered input-sm w-full font-mono text-xs" />
                  </div>
                  <div class="mb-2">
                    <label class="label py-1"><span class="label-text text-xs">Template Message Body</span></label>
                    <textarea v-model="localForm.callPermissionTemplateBody" class="textarea textarea-bordered textarea-sm w-full text-xs" rows="2"></textarea>
                  </div>
                  <div v-if="isEdit === true && hasValidSourceId === true" class="mt-2 flex items-center">
                    <button type="button" class="btn btn-sm" :disabled="creatingTemplateInProgress === true" @click="createCallPermissionTemplateDirectly">
                      {{ creatingTemplateInProgress === true ? 'Creating…' : 'Create Template in Meta Now' }}
                    </button>
                    <span v-if="templateCreatedSuccess === true" class="text-xs text-green-600 ml-2 font-medium">✓ Template created</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="selectedProvider === 'smooch'">
          <div>
            <label class="label"><span class="label-text">Name</span></label>
            <input type="text" v-model="localForm.name" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Phone Number</span></label>
            <input type="text" v-model="localForm.phone" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">App ID</span></label>
            <input type="text" v-model="localForm.appId" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">App Key</span></label>
            <input type="password" v-model="localForm.appKey" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Secret</span></label>
            <input type="password" v-model="localForm.secret" class="input input-bordered w-full" />
          </div>
        </div>

        <div v-if="selectedProvider === 'digital99'">
          <div v-if="isEdit === true" class="webhook-card digital99-webhook-card">
            <div class="webhook-row">
              <span>Attach this 99 source to the AI platform webhook.</span>
            </div>
            <div class="webhook-code mt-2">{{ digital99Webhook }}</div>
            <div class="mt-2">
              <button type="button" class="btn btn-sm" :disabled="hasSourceId !== true" @click="emitAttachWebhook">
                <Icon icon="tabler:webhook" width="16" />
                <span class="ml-1">Attach to AI platform</span>
              </button>
            </div>
          </div>
          <div>
            <label class="label"><span class="label-text">Name</span></label>
            <input type="text" v-model="localForm.name" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Phone Number</span></label>
            <input type="text" v-model="localForm.phone" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">API Key</span></label>
            <input type="password" v-model="localForm.apiKey" class="input input-bordered w-full" />
          </div>
        </div>

        <div v-if="selectedProvider === 'twilioPhone'">
          <div>
            <label class="label"><span class="label-text">Name</span></label>
            <input type="text" v-model="localForm.name" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Twilio Connection Type</span></label>
            <select v-model="localForm.twilioConnectionType" class="select select-bordered w-full">
              <option value="websocket">WebSocket</option>
              <option value="sipTrunk">SIP Trunk</option>
            </select>
          </div>
          <div>
            <label class="label"><span class="label-text">Phone Number</span></label>
            <input type="text" v-model="localForm.phone" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Account SID</span></label>
            <input type="text" v-model="localForm.accountSid" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Auth Token</span></label>
            <input type="password" v-model="localForm.authToken" class="input input-bordered w-full" />
          </div>
          <div v-if="localForm.twilioConnectionType === 'sipTrunk'">
            <label class="label"><span class="label-text">SIP Trunk SID</span></label>
            <input type="text" v-model="localForm.twilioSipTrunkSid" class="input input-bordered w-full" />
          </div>
          <div v-if="localForm.twilioConnectionType === 'sipTrunk'">
            <label class="label"><span class="label-text">Termination SIP URI</span></label>
            <input type="text" v-model="localForm.twilioSipUri" class="input input-bordered w-full" />
          </div>
          <div v-if="localForm.twilioConnectionType === 'websocket'">
            <label class="label"><span class="label-text">Media WebSocket URL</span></label>
            <input type="text" v-model="localForm.twilioWebsocketUrl" class="input input-bordered w-full" />
          </div>
        </div>

        <div v-if="selectedProvider === 'customSip'">
          <div>
            <label class="label"><span class="label-text">Name</span></label>
            <input type="text" v-model="localForm.name" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Incoming Phone Number (DID)</span></label>
            <input type="text" v-model="localForm.phone" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Forwarding SIP URI</span></label>
            <input type="text" v-model="localForm.forwardUri" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Allowed Source IP</span></label>
            <input type="text" v-model="localForm.allowedIp" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">SIP Username</span></label>
            <input type="text" v-model="localForm.sipUsername" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">SIP Password</span></label>
            <input type="password" v-model="localForm.sipPassword" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">SIP Domain</span></label>
            <input type="text" v-model="localForm.sipDomain" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Successful SIP Response Codes</span></label>
            <input type="text" v-model="localForm.successResponseCodes" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">AI Started Talking Code</span></label>
            <input type="text" v-model="localForm.aiStartedCode" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Drop Call On Hold Code</span></label>
            <input type="text" v-model="localForm.dropOnHoldCode" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Transfer Method</span></label>
            <select v-model="localForm.transferMethod" class="select select-bordered w-full">
              <option value="infer">Infer</option>
              <option value="header">Header</option>
            </select>
          </div>
          <div>
            <label class="label"><span class="label-text">Transfer Header Name</span></label>
            <input type="text" v-model="localForm.transferHeaderName" class="input input-bordered w-full" />
          </div>
        </div>

        <div v-if="selectedProvider === 'partner'">
          <div class="webhook-card">
            <span>Partner selected. No fields required.</span>
          </div>
        </div>

        <div v-if="selectedProvider === 'twilioSms'">
          <div>
            <label class="label"><span class="label-text">Name</span></label>
            <input type="text" v-model="localForm.name" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Sender Number</span></label>
            <input type="text" v-model="localForm.phone" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Account SID</span></label>
            <input type="text" v-model="localForm.accountSid" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Auth Token</span></label>
            <input type="password" v-model="localForm.authToken" class="input input-bordered w-full" />
          </div>
        </div>

        <div v-if="selectedProvider === 'maskyoo'">
          <div>
            <label class="label"><span class="label-text">Name</span></label>
            <input type="text" v-model="localForm.name" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Sender</span></label>
            <input type="text" v-model="localForm.sender" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Bearer Token</span></label>
            <input type="password" v-model="localForm.bearerToken" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Base URL</span></label>
            <input type="text" v-model="localForm.baseUrl" class="input input-bordered w-full" />
          </div>
        </div>

        <div v-if="selectedProvider === 'smsQuota'">
          <div>
            <label class="label"><span class="label-text">Name</span></label>
            <input type="text" v-model="localForm.name" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">External SMS Service URL</span></label>
            <input type="text" v-model="localForm.baseUrl" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Path</span></label>
            <input type="text" v-model="localForm.path" class="input input-bordered w-full" />
          </div>
        </div>

        <div v-if="selectedProvider === 'microsoft365'">
          <div>
            <label class="label"><span class="label-text">Name</span></label>
            <input type="text" v-model="localForm.name" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Sender Email</span></label>
            <input type="text" v-model="localForm.senderEmail" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Tenant ID</span></label>
            <input type="text" v-model="localForm.tenantId" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Client ID</span></label>
            <input type="text" v-model="localForm.clientId" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Client Secret</span></label>
            <input type="password" v-model="localForm.clientSecret" class="input input-bordered w-full" />
          </div>
        </div>
        <div v-if="selectedProvider === 'gmail'">
          <div>
            <label class="label"><span class="label-text">Name</span></label>
            <input type="text" v-model="localForm.name" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Sender Email</span></label>
            <input type="text" v-model="localForm.senderEmail" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Google Credentials JSON</span></label>
            <input type="file" accept=".json,application/json" class="file-input file-input-bordered w-full" @change="handleGmailCredentialsUpload" />
            <div v-if="localForm.googleCredentialsFileName !== ''" class="text-xs text-gray-500 mt-2">{{ localForm.googleCredentialsFileName }}</div>
            <div v-if="gmailCredentialsError !== ''" class="text-xs text-red-500 mt-2">{{ gmailCredentialsError }}</div>
          </div>
          <div>
            <label class="label"><span class="label-text">Refresh Token</span></label>
            <input type="password" v-model="localForm.refreshToken" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Client ID</span></label>
            <input type="text" v-model="localForm.clientId" class="input input-bordered w-full" />
          </div>
          <div>
            <label class="label"><span class="label-text">Client Secret</span></label>
            <input type="password" v-model="localForm.clientSecret" class="input input-bordered w-full" />
          </div>
        </div>
      </div>

      <div class="modal-action">
        <button class="btn" @click="emitClose">Cancel</button>
        <button class="btn" @click="emitSave">Save</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop"><button @click="emitClose">close</button></form>
  </div>
</template>

<script setup>
import { reactive, watch, computed, ref } from 'vue';
import { Icon } from '@iconify/vue';
import { globalState } from '@/globalState';
import axios from '@/api/axiosInstance';
import { useToast } from 'vue-toastification';
import { useI18n } from '@/i18n.js';

const props = defineProps({
  visible: { type: Boolean, default: false },
  type: { type: String, default: '' },
  model: { type: Object, default: () => ({}) },
  sourceData: { type: Object, default: () => ({}) },
  isEdit: { type: Boolean, default: false },
  sourceId: { type: [Number, String], default: '' }
});

const emit = defineEmits(['close', 'save', 'attachWebhook']);
const selectedChannel = ref('whatsapp');
const selectedProvider = ref('meta');

const channelOptions = [
  { key: 'whatsapp', label: 'WhatsApp', icon: 'tabler:brand-whatsapp' },
  { key: 'phone', label: 'Phone', icon: 'tabler:phone' },
  { key: 'sms', label: 'SMS', icon: 'tabler:message' },
  { key: 'email', label: 'Email', icon: 'tabler:mail' }
];

const providerOptionsByChannel = {
  whatsapp: [
    { key: 'meta', label: 'Meta', icon: 'tabler:brand-meta' },
    { key: 'digital99', label: '99', icon: 'tabler:brand-whatsapp' },
    { key: 'smooch', label: 'Smooch', icon: 'tabler:message-circle' }
  ],
  phone: [
    { key: 'partner', label: 'Partner', icon: 'tabler:building-bank' },
    { key: 'twilioPhone', label: 'Twilio', icon: 'tabler:phone-call' },
    { key: 'customSip', label: 'Custom SIP', icon: 'tabler:phone-calling' }
  ],
  sms: [
    { key: 'smsQuota', label: 'SMS Quota', icon: 'tabler:message' },
    { key: 'maskyoo', label: 'Maskyoo', icon: 'tabler:message' },
    { key: 'twilioSms', label: 'Twilio', icon: 'tabler:device-mobile-message' }
  ],
  email: [
    { key: 'microsoft365', label: 'Outlook', icon: 'tabler:brand-office' },
    { key: 'gmail', label: 'Gmail', icon: 'tabler:brand-google' }
  ]
};

const localForm = reactive({
  name: '',
  wabaId: '',
  phoneNumberId: '',
  phoneNumber: '',
  phone: '',
  from: '',
  accessToken: '',
  appSecret: '',
  verifyToken: '',
  appId: '',
  appKey: '',
  secret: '',
  accountSid: '',
  authToken: '',
  sender: '',
  bearerToken: '',
  baseUrl: '',
  path: '',
  twilioConnectionType: 'websocket',
  twilioSipTrunkSid: '',
  twilioSipUri: '',
  twilioWebsocketUrl: '',
  apiKey: '',
  forwardUri: '',
  allowedIp: '',
  sipUsername: '',
  sipPassword: '',
  sipDomain: '',
  successResponseCodes: '',
  aiStartedCode: '',
  dropOnHoldCode: '',
  transferMethod: 'infer',
  transferHeaderName: '',
  senderEmail: '',
  tenantId: '',
  clientId: '',
  clientSecret: '',
  refreshToken: '',
  emailAddress: '',
  googleCredentialsFileName: '',
  voiceEnabled: false,
  voiceCallIconVisibility: 'default',
  voiceCallbackPermissionEnabled: true,
  customDomain: '',
  voiceCustomDomain: '',
  createCallPermissionTemplate: false,
  callPermissionTemplateLanguage: 'he',
  callPermissionTemplateName: 'callpermissionrequesthe',
  callPermissionTemplateBody: 'שלום, אנו מבקשים את אישורך לקבלת שיחה טלפונית בוואטסאפ מנציגנו.'
});
const toast = useToast();
const { currentLanguage } = useI18n();
const creatingTemplateInProgress = ref(false);
const templateCreatedSuccess = ref(false);

const callPermissionLanguageOptions = [
  { code: 'he', label: 'עברית (Hebrew)' },
  { code: 'en_US', label: 'English (US)' },
  { code: 'en_GB', label: 'English (UK)' },
  { code: 'ar', label: 'العربية (Arabic)' },
  { code: 'es', label: 'Español (Spanish)' },
  { code: 'ru', label: 'Русский (Russian)' },
  { code: 'fr', label: 'Français (French)' }
];

const callPermissionLanguagePresets = {
  he: {
    code: 'he',
    name: 'callpermissionrequesthe',
    body: 'שלום, אנו מבקשים את אישורך לקבלת שיחה טלפונית בוואטסאפ מנציגנו.'
  },
  en_US: {
    code: 'en_US',
    name: 'callpermissionrequesten',
    body: 'Hello, we would like to request your permission to call you on WhatsApp.'
  },
  en_GB: {
    code: 'en_GB',
    name: 'callpermissionrequestengb',
    body: 'Hello, we would like to request your permission to call you on WhatsApp.'
  },
  ar: {
    code: 'ar',
    name: 'callpermissionrequestar',
    body: 'مرحباً، نطلب موافقتك لتلقي مكالمة هاتفية عبر واتساب.'
  },
  es: {
    code: 'es',
    name: 'callpermissionrequestes',
    body: 'Hola, solicitamos su permiso para comunicarnos mediante una llamada de voz en WhatsApp.'
  },
  ru: {
    code: 'ru',
    name: 'callpermissionrequestru',
    body: 'Здравствуйте, мы запрашиваем ваше разрешение на голосовой звонок в WhatsApp.'
  },
  fr: {
    code: 'fr',
    name: 'callpermissionrequestfr',
    body: 'Bonjour, nous souhaitons solliciter votre autorisation pour vous contacter par appel WhatsApp.'
  }
};

function resolveDefaultCallPermissionLanguage() {
  const lang = typeof currentLanguage.value === 'string' ? currentLanguage.value.toLowerCase() : '';
  if (lang === 'he') {
    return 'he';
  }
  if (lang === 'il') {
    return 'he';
  }
  if (lang === 'ar') {
    return 'ar';
  }
  if (lang === 'es') {
    return 'es';
  }
  if (lang === 'ru') {
    return 'ru';
  }
  if (lang === 'fr') {
    return 'fr';
  }
  return 'en_US';
}

function handleCallPermissionLanguageChange() {
  const selectedLang = localForm.callPermissionTemplateLanguage;
  const preset = callPermissionLanguagePresets[selectedLang];
  if (preset !== undefined) {
    if (preset !== null) {
      localForm.callPermissionTemplateName = preset.name;
      localForm.callPermissionTemplateBody = preset.body;
    }
  }
}

const hasValidSourceId = computed(() => {
  if (typeof props.sourceId === 'number') {
    if (props.sourceId > 0) {
      return true;
    }
  }
  if (typeof props.sourceId === 'string') {
    if (props.sourceId.trim().length > 0) {
      return true;
    }
  }
  if (props.sourceData !== undefined) {
    if (props.sourceData !== null) {
      if (typeof props.sourceData.sourceId === 'number') {
        if (props.sourceData.sourceId > 0) {
          return true;
        }
      }
      if (typeof props.sourceData.sourceId === 'string') {
        if (props.sourceData.sourceId.trim().length > 0) {
          return true;
        }
      }
    }
  }
  if (props.model !== undefined) {
    if (props.model !== null) {
      if (typeof props.model.sourceId === 'number') {
        if (props.model.sourceId > 0) {
          return true;
        }
      }
      if (typeof props.model.sourceId === 'string') {
        if (props.model.sourceId.trim().length > 0) {
          return true;
        }
      }
    }
  }
  return false;
});

async function createCallPermissionTemplateDirectly() {
  let targetSourceId = '';
  if (typeof props.sourceId === 'number') {
    if (props.sourceId > 0) {
      targetSourceId = String(props.sourceId);
    }
  }
  if (targetSourceId === '') {
    if (typeof props.sourceId === 'string') {
      if (props.sourceId.trim().length > 0) {
        targetSourceId = props.sourceId.trim();
      }
    }
  }
  if (targetSourceId === '') {
    if (props.sourceData !== undefined) {
      if (props.sourceData !== null) {
        if (props.sourceData.sourceId !== undefined) {
          if (props.sourceData.sourceId !== null) {
            targetSourceId = String(props.sourceData.sourceId).trim();
          }
        }
      }
    }
  }
  if (targetSourceId === '') {
    if (props.model !== undefined) {
      if (props.model !== null) {
        if (props.model.sourceId !== undefined) {
          if (props.model.sourceId !== null) {
            targetSourceId = String(props.model.sourceId).trim();
          }
        }
      }
    }
  }
  if (targetSourceId === '') {
    toast.error('Source ID not found. Save the source first.');
    return;
  }
  creatingTemplateInProgress.value = true;
  templateCreatedSuccess.value = false;
  try {
    await axios.post(`${globalState.apiEndpoint}/api/communicationSources/whatsappMeta/${targetSourceId}/callPermissionTemplate`, {
      languageCode: localForm.callPermissionTemplateLanguage,
      templateName: localForm.callPermissionTemplateName,
      bodyText: localForm.callPermissionTemplateBody
    });
    creatingTemplateInProgress.value = false;
    templateCreatedSuccess.value = true;
    toast.success('Call permission template created successfully in Meta.');
  } catch (error) {
    creatingTemplateInProgress.value = false;
    let errorMsg = 'Failed to create call permission template in Meta.';
    if (error?.response?.data?.details !== undefined) {
      if (typeof error.response.data.details === 'string') {
        if (error.response.data.details.length > 0) {
          errorMsg = error.response.data.details;
        }
      }
    }
    toast.error(errorMsg);
  }
}

const gmailCredentialsError = ref('');

const providerOptions = computed(() => {
  if (providerOptionsByChannel[selectedChannel.value] !== undefined) {
    return providerOptionsByChannel[selectedChannel.value];
  }
  return [];
});

const providerButtonClass = computed(() => {
  const count = providerOptions.value.length;
  if (count === 1) { return 'select-chip-provider-1'; }
  if (count === 2) { return 'select-chip-provider-2'; }
  return 'select-chip-provider-3';
});

const hasSourceId = computed(() => {
  const value = props.sourceId;
  if (typeof value === 'number' && Number.isFinite(value) === true) {
    return value > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return false;
});

const digital99Webhook = computed(() => {
  const apiEndpoint = typeof globalState?.apiEndpoint === 'string' ? globalState.apiEndpoint.replace(/\/+$/, '') : '';
  const originValue = apiEndpoint.length > 0 ? apiEndpoint : (typeof window !== 'undefined' && window.location !== undefined && typeof window.location.origin === 'string' ? window.location.origin : '');
  return `${originValue}/api/whatsapp/99digital/webhook`;
});

function mapTypeToSelection(rawType) {
  if (rawType === 'whatsappMeta') {
    return { channel: 'whatsapp', provider: 'meta' };
  }
  if (rawType === 'whatsapp99Digital') {
    return { channel: 'whatsapp', provider: 'digital99' };
  }
  if (rawType === 'whatsappSmooch') {
    return { channel: 'whatsapp', provider: 'smooch' };
  }
  if (rawType === 'twilioPhone') {
    return { channel: 'phone', provider: 'twilioPhone' };
  }
  if (rawType === 'phonePartner') {
    return { channel: 'phone', provider: 'partner' };
  }
  if (rawType === 'phoneCustomSip') {
    return { channel: 'phone', provider: 'customSip' };
  }
  if (rawType === 'smsTwilio') {
    return { channel: 'sms', provider: 'twilioSms' };
  }
  if (rawType === 'smsMaskyoo') {
    return { channel: 'sms', provider: 'maskyoo' };
  }
  if (rawType === 'smsQuota') {
    return { channel: 'sms', provider: 'smsQuota' };
  }
  if (rawType === 'emailMicrosoft365') {
    return { channel: 'email', provider: 'microsoft365' };
  }
  if (rawType === 'emailGoogleGmail') {
    return { channel: 'email', provider: 'gmail' };
  }
  return { channel: 'whatsapp', provider: 'meta' };
}

function mapSelectionToType(channel, provider) {
  if (channel === 'whatsapp' && provider === 'meta') {
    return 'whatsappMeta';
  }
  if (channel === 'whatsapp' && provider === 'digital99') {
    return 'whatsapp99Digital';
  }
  if (channel === 'whatsapp' && provider === 'smooch') {
    return 'whatsappSmooch';
  }
  if (channel === 'phone' && provider === 'twilioPhone') {
    return 'twilioPhone';
  }
  if (channel === 'phone' && provider === 'partner') {
    return 'phonePartner';
  }
  if (channel === 'phone' && provider === 'customSip') {
    return 'phoneCustomSip';
  }
  if (channel === 'sms' && provider === 'twilioSms') {
    return 'smsTwilio';
  }
  if (channel === 'sms' && provider === 'maskyoo') {
    return 'smsMaskyoo';
  }
  if (channel === 'sms' && provider === 'smsQuota') {
    return 'smsQuota';
  }
  if (channel === 'email' && provider === 'microsoft365') {
    return 'emailMicrosoft365';
  }
  if (channel === 'email' && provider === 'gmail') {
    return 'emailGoogleGmail';
  }
  return 'whatsappMeta';
}

function getGoogleCredentialsConfig(rawValue) {
  if (rawValue === undefined || rawValue === null || typeof rawValue !== 'object' || Array.isArray(rawValue) === true) {
    return {};
  }
  if (rawValue.installed !== undefined && rawValue.installed !== null && typeof rawValue.installed === 'object' && Array.isArray(rawValue.installed) !== true) {
    return rawValue.installed;
  }
  if (rawValue.web !== undefined && rawValue.web !== null && typeof rawValue.web === 'object' && Array.isArray(rawValue.web) !== true) {
    return rawValue.web;
  }
  return rawValue;
}

function applyGmailCredentialsData(rawValue, fileName) {
  const payload = getGoogleCredentialsConfig(rawValue);
  const clientId = typeof payload.client_id === 'string' ? payload.client_id : (typeof payload.clientId === 'string' ? payload.clientId : '');
  const clientSecret = typeof payload.client_secret === 'string' ? payload.client_secret : (typeof payload.clientSecret === 'string' ? payload.clientSecret : '');
  const refreshToken = typeof rawValue.refresh_token === 'string' ? rawValue.refresh_token : (typeof rawValue.refreshToken === 'string' ? rawValue.refreshToken : (typeof payload.refresh_token === 'string' ? payload.refresh_token : (typeof payload.refreshToken === 'string' ? payload.refreshToken : '')));
  const senderEmail = typeof rawValue.senderEmail === 'string' ? rawValue.senderEmail : (typeof rawValue.emailAddress === 'string' ? rawValue.emailAddress : (typeof rawValue.user === 'string' ? rawValue.user : (typeof payload.senderEmail === 'string' ? payload.senderEmail : (typeof payload.emailAddress === 'string' ? payload.emailAddress : (typeof payload.user === 'string' ? payload.user : '')))));
  if (clientId === '' || clientSecret === '') {
    gmailCredentialsError.value = 'The uploaded JSON is missing client ID or client secret.';
    return;
  }
  gmailCredentialsError.value = '';
  localForm.clientId = clientId;
  localForm.clientSecret = clientSecret;
  if (refreshToken !== '') {
    localForm.refreshToken = refreshToken;
  }
  if (senderEmail !== '') {
    localForm.senderEmail = senderEmail;
    localForm.emailAddress = senderEmail;
  }
  localForm.googleCredentialsFileName = typeof fileName === 'string' ? fileName : '';
}

async function handleGmailCredentialsUpload(event) {
  const target = event !== undefined && event !== null ? event.target : null;
  const files = target !== null && target !== undefined ? target.files : null;
  const file = files !== null && files !== undefined && files.length > 0 ? files[0] : null;
  if (file === null) {
    return;
  }
  try {
    const text = await file.text();
    const parsed = JSON.parse(text);
    applyGmailCredentialsData(parsed, file.name);
  } catch (error) {
    gmailCredentialsError.value = 'Failed to read the uploaded JSON file.';
  }
}

watch(() => props.type, (val) => {
  const mapped = mapTypeToSelection(val);
  selectedChannel.value = mapped.channel;
  selectedProvider.value = mapped.provider;
}, { immediate: true });

watch(() => props.model, (val) => {
  gmailCredentialsError.value = '';
  if (val !== undefined && val !== null) {
    localForm.name = typeof val.name === 'string' ? val.name : '';
    localForm.wabaId = typeof val.wabaId === 'string' ? val.wabaId : '';
    localForm.phoneNumberId = typeof val.phoneNumberId === 'string' ? val.phoneNumberId : '';
    localForm.phoneNumber = typeof val.phoneNumber === 'string' ? val.phoneNumber : (typeof val.phone === 'string' ? val.phone : '');
    localForm.phone = typeof val.phone === 'string' ? val.phone : '';
    localForm.from = typeof val.from === 'string' ? val.from : '';
    localForm.accessToken = typeof val.accessToken === 'string' ? val.accessToken : '';
    localForm.appSecret = typeof val.appSecret === 'string' ? val.appSecret : '';
    localForm.verifyToken = typeof val.verifyToken === 'string' ? val.verifyToken : '';
    localForm.appId = typeof val.appId === 'string' ? val.appId : '';
    localForm.appKey = typeof val.appKey === 'string' ? val.appKey : '';
    localForm.secret = typeof val.secret === 'string' ? val.secret : '';
    localForm.accountSid = typeof val.accountSid === 'string' ? val.accountSid : '';
    localForm.authToken = typeof val.authToken === 'string' ? val.authToken : '';
    localForm.sender = typeof val.sender === 'string' ? val.sender : '';
    localForm.bearerToken = typeof val.bearerToken === 'string' ? val.bearerToken : '';
    localForm.baseUrl = typeof val.baseUrl === 'string' ? val.baseUrl : '';
    localForm.path = typeof val.path === 'string' ? val.path : '';
    localForm.twilioConnectionType = typeof val.twilioConnectionType === 'string' && val.twilioConnectionType.length > 0 ? val.twilioConnectionType : 'websocket';
    localForm.twilioSipTrunkSid = typeof val.twilioSipTrunkSid === 'string' ? val.twilioSipTrunkSid : '';
    localForm.twilioSipUri = typeof val.twilioSipUri === 'string' ? val.twilioSipUri : '';
    localForm.twilioWebsocketUrl = typeof val.twilioWebsocketUrl === 'string' ? val.twilioWebsocketUrl : '';
    localForm.apiKey = typeof val.apiKey === 'string' ? val.apiKey : (typeof val.accessToken === 'string' ? val.accessToken : '');
    localForm.forwardUri = typeof val.forwardUri === 'string' ? val.forwardUri : '';
    localForm.allowedIp = typeof val.allowedIp === 'string' ? val.allowedIp : '';
    localForm.sipUsername = typeof val.sipUsername === 'string' ? val.sipUsername : '';
    localForm.sipPassword = typeof val.sipPassword === 'string' ? val.sipPassword : '';
    localForm.sipDomain = typeof val.sipDomain === 'string' ? val.sipDomain : '';
    localForm.successResponseCodes = typeof val.successResponseCodes === 'string' ? val.successResponseCodes : '';
    localForm.aiStartedCode = typeof val.aiStartedCode === 'string' ? val.aiStartedCode : '';
    localForm.dropOnHoldCode = typeof val.dropOnHoldCode === 'string' ? val.dropOnHoldCode : '';
    localForm.transferMethod = typeof val.transferMethod === 'string' && val.transferMethod.length > 0 ? val.transferMethod : 'infer';
    localForm.transferHeaderName = typeof val.transferHeaderName === 'string' ? val.transferHeaderName : '';
    localForm.senderEmail = typeof val.senderEmail === 'string' ? val.senderEmail : (typeof val.user === 'string' ? val.user : (typeof val.userId === 'string' ? val.userId : ''));
    localForm.tenantId = typeof val.tenantId === 'string' ? val.tenantId : '';
    localForm.clientId = typeof val.clientId === 'string' ? val.clientId : '';
    localForm.clientSecret = typeof val.clientSecret === 'string' ? val.clientSecret : '';
    localForm.refreshToken = typeof val.refreshToken === 'string' ? val.refreshToken : (typeof val.googleRefreshToken === 'string' ? val.googleRefreshToken : (typeof val.gmailRefreshToken === 'string' ? val.gmailRefreshToken : ''));
    localForm.emailAddress = typeof val.emailAddress === 'string' ? val.emailAddress : '';
    localForm.googleCredentialsFileName = typeof val.googleCredentialsFileName === 'string' ? val.googleCredentialsFileName : '';
    let customDomainVal = '';
    if (typeof val.customDomain === 'string') {
      customDomainVal = val.customDomain;
    } else if (typeof val.normalCustomDomain === 'string') {
      customDomainVal = val.normalCustomDomain;
    } else if (typeof val.domain === 'string') {
      customDomainVal = val.domain;
    }
    localForm.customDomain = customDomainVal;
    let voiceCustomDomainVal = '';
    if (typeof val.voiceCustomDomain === 'string') {
      voiceCustomDomainVal = val.voiceCustomDomain;
    } else if (typeof val.voiceDomain === 'string') {
      voiceCustomDomainVal = val.voiceDomain;
    }
    localForm.voiceCustomDomain = voiceCustomDomainVal;
    localForm.voiceEnabled = val.voiceEnabled === true;
    let rawVisibility = '';
    if (typeof val.voiceCallIconVisibility === 'string') {
      rawVisibility = val.voiceCallIconVisibility;
    }
    if (rawVisibility === 'DISABLE_ALL' || rawVisibility === 'disableAll') {
      localForm.voiceCallIconVisibility = 'disableAll';
    } else {
      localForm.voiceCallIconVisibility = 'default';
    }
    localForm.voiceCallbackPermissionEnabled = val.voiceCallbackPermissionEnabled !== false;
    localForm.createCallPermissionTemplate = val.createCallPermissionTemplate === true;
    let templateLang = resolveDefaultCallPermissionLanguage();
    if (typeof val.callPermissionTemplateLanguage === 'string') {
      if (val.callPermissionTemplateLanguage.trim().length > 0) {
        templateLang = val.callPermissionTemplateLanguage.trim();
      }
    }
    localForm.callPermissionTemplateLanguage = templateLang;
    const preset = callPermissionLanguagePresets[templateLang];
    let defaultTemplateName = 'callpermissionrequesten';
    let defaultTemplateBody = 'Hello, we would like to request your permission to call you on WhatsApp.';
    if (preset !== undefined) {
      if (preset !== null) {
        defaultTemplateName = preset.name;
        defaultTemplateBody = preset.body;
      }
    }
    if (typeof val.callPermissionTemplateName === 'string') {
      if (val.callPermissionTemplateName.trim().length > 0) {
        defaultTemplateName = val.callPermissionTemplateName.trim();
      }
    }
    if (typeof val.callPermissionTemplateBody === 'string') {
      if (val.callPermissionTemplateBody.trim().length > 0) {
        defaultTemplateBody = val.callPermissionTemplateBody.trim();
      }
    }
    localForm.callPermissionTemplateName = defaultTemplateName;
    localForm.callPermissionTemplateBody = defaultTemplateBody;
  }
}, { immediate: true });

function selectChannel(channelKey) {
  if (props.isEdit === true) {
    return;
  }
  selectedChannel.value = channelKey;
  const list = providerOptionsByChannel[channelKey];
  if (Array.isArray(list) === true && list.length > 0) {
    selectedProvider.value = list[0].key;
  }
}

function selectProvider(providerKey) {
  if (props.isEdit === true) {
    return;
  }
  selectedProvider.value = providerKey;
}

function emitClose() {
  emit('close');
}

function emitAttachWebhook() {
  emit('attachWebhook', { sourceId: props.sourceId });
}

function emitSave() {
  const sourceType = mapSelectionToType(selectedChannel.value, selectedProvider.value);
  const config = selectedProvider.value === 'partner'
    ? { provider: selectedProvider.value, channel: selectedChannel.value }
    : selectedProvider.value === 'meta'
      ? {
          name: localForm.name,
          wabaId: localForm.wabaId,
          phoneNumberId: localForm.phoneNumberId,
          phoneNumber: localForm.phoneNumber,
          apiKey: localForm.apiKey,
          appSecret: localForm.appSecret,
          customDomain: localForm.customDomain,
          voiceCustomDomain: localForm.voiceCustomDomain,
          voiceEnabled: localForm.voiceEnabled === true,
          voiceCallIconVisibility: localForm.voiceCallIconVisibility,
          voiceCallbackPermissionEnabled: localForm.voiceCallbackPermissionEnabled === true,
          createCallPermissionTemplate: localForm.createCallPermissionTemplate === true,
          callPermissionTemplateLanguage: localForm.callPermissionTemplateLanguage,
          callPermissionTemplateName: localForm.callPermissionTemplateName,
          callPermissionTemplateBody: localForm.callPermissionTemplateBody,
          provider: selectedProvider.value,
          channel: selectedChannel.value
        }
      : selectedProvider.value === 'microsoft365'
        ? { ...localForm, user: localForm.senderEmail, provider: selectedProvider.value, channel: selectedChannel.value }
        : selectedProvider.value === 'gmail'
          ? { ...localForm, user: localForm.senderEmail, emailAddress: localForm.senderEmail, provider: selectedProvider.value, channel: selectedChannel.value }
        : { ...localForm, provider: selectedProvider.value, channel: selectedChannel.value };
  emit('save', { type: sourceType, config });
}
</script>

<style scoped>
.modal {
  overflow-y: auto;
}
.modal-box {
  max-height: calc(100vh - 32px);
  overflow-y: auto;
}
.selector-row {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
}
.selector-row-channel {
  direction: ltr;
}
.selector-row-provider {
  direction: ltr;
}
.select-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  margin: 4px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  line-height: 1.2;
  padding: 0 8px;
  flex: 1 1 120px;
  overflow: hidden;
}
.select-chip-channel {
  flex: 0 0 calc(25% - 8px);
}
.select-chip-provider-1 {
  flex: 0 0 calc(100% - 8px);
}
.select-chip-provider-2 {
  flex: 0 0 calc(50% - 8px);
}
.select-chip-provider-3 {
  flex: 0 0 calc(33.333% - 8px);
}
.select-chip-active {
  border-color: #4f46e5;
  color: #4f46e5;
  background: rgba(79, 70, 229, 0.08);
}
.select-chip-content {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}
.select-chip-label {
  margin-inline-start: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.source-fields > div {
  margin-bottom: 12px;
}
.source-fields > div:last-child {
  margin-bottom: 0;
}
.webhook-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 10px 12px;
}
@media (max-width: 900px) {
  .select-chip-channel {
    flex: 0 0 calc(50% - 8px);
  }
  .select-chip-provider-3 {
    flex: 0 0 calc(50% - 8px);
  }
}
</style>
